import isObject from 'lodash/isObject';

import logger from './logger';

type RxDBWriteErrorLike = { status?: number };

const isValidationError = (e: unknown): boolean =>
  isObject(e) && (e as RxDBWriteErrorLike).status === 422;

export default async function handleValidationErrorsCleanly<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  executeQuery: (...args: any[]) => Promise<T>,
): Promise<T> {
  function logValidationFailure(errors: unknown[]) {
    logger.error(
      [
        'Validation failed!',
        'This could be caused by some client-side code or old/bad data in your Postgres database.',
        'We have a snippet you can run to fix the data in your database. How you run it depends on the backend you are pointing at;',
        '- Local backend via Docker: run `make shell` at the root of the project, copy-paste the contents of `snippets/fix_validation_errors_for_all_docs.py`, and press enter.',
        '- Local backend, no Docker: run `python snippets/fix_validation_errors_for_all_docs.py` from the root of the project.',
        '- Production: *carefully* run `snippets/fix_validation_errors_for_all_docs.py` against the account in question.',
        'Note: some schemas have `allOf` which contains business logic validation rules.',
        '',
        JSON.stringify(errors, null, 2),
        '',
      ].join('\n\n'),
      {
        executeQuery,
        stack: new Error('Dummy error to get stack').stack,
      },
    );
  }

  function logOtherWriteFailure(errors: unknown[]) {
    logger.error(
      `Database write failed (non-validation): ${JSON.stringify(errors, null, 2)}`,
      {
        executeQuery,
        stack: new Error('Dummy error to get stack').stack,
      },
    );
  }

  let result: Awaited<ReturnType<typeof executeQuery>>;
  try {
    result = await executeQuery();
    if (isObject(result) && 'error' in result) {
      // Only an array is a bulk-write error list — a document whose *data* has
      // an `error` field (e.g. goodreads_imports.error, a string) lands here too.
      const errors: unknown[] | null = Array.isArray(result.error) ? result.error : null;
      if (errors?.length) {
        if (errors.some(isValidationError)) {
          logValidationFailure(errors);
        } else {
          logOtherWriteFailure(errors);
        }
      }
    }
    return result;
  } catch (error) {
    if (error instanceof Error && error.message.includes('RxError (VD2)')) {
      logValidationFailure([error]);
    }
    throw error;
  }
}
